import React, { useEffect, useMemo, useState } from "react";
import { Table, Button, Modal, message, Empty, Tooltip } from "antd";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faRotate } from "@fortawesome/free-solid-svg-icons";
import rewardProgramApi from "../../../api/rewardProgramApi";
import { buildRewardProgramColumns } from "./reward-program-list-columns";
import { RewardProgramStats, RewardProgramFilters, filterPrograms } from "./reward-program-list-toolbar";

/** Danh sách đợt Chương trình thưởng (nền tảng mới, tách "Chiến dịch X2 cũ"). */
function RewardProgramList() {
    const navigate = useNavigate();
    const [programs, setPrograms] = useState([]);
    const [types, setTypes] = useState({});
    const [loading, setLoading] = useState(false);
    // Bộ lọc client — API trả cả danh sách nên không cần round-trip khi đổi tab.
    const [status, setStatus] = useState("all");
    const [keyword, setKeyword] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [campFilter, setCampFilter] = useState("");
    const [enabledOnly, setEnabledOnly] = useState(false);

    const fetchAll = async () => {
        setLoading(true);
        try {
            const [progRes, typeRes] = await Promise.all([rewardProgramApi.list(), rewardProgramApi.getTypes()]);
            setPrograms(progRes.data.programs || []);
            setTypes(Object.fromEntries((typeRes.data.types || []).map((t) => [t.key, t.label])));
        } catch {
            message.error("Không thể tải danh sách chương trình thưởng");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleClone = async (record) => {
        try {
            const { data } = await rewardProgramApi.clone(record.id);
            message.success("Đã nhân bản — sửa slug/ngày rồi bật");
            navigate(`/reward-program/${data.program.id}/edit`);
        } catch {
            message.error("Nhân bản thất bại");
        }
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: "Xoá chương trình?",
            content: `Chỉ xoá được đợt CHƯA phát sinh sổ thưởng. Xoá "${record.name}"?`,
            okText: "Xoá", okButtonProps: { danger: true }, cancelText: "Huỷ",
            onOk: async () => {
                try {
                    await rewardProgramApi.remove(record.id);
                    message.success("Đã xoá");
                    fetchAll();
                } catch (err) {
                    message.error(err?.response?.data?.message || "Xoá thất bại");
                }
            },
        });
    };

    const columns = useMemo(
        () => buildRewardProgramColumns({
            typeLabels: types,
            onOpen: (r) => navigate(`/reward-program/${r.id}`),
            onEdit: (r) => navigate(`/reward-program/${r.id}/edit`),
            onClone: handleClone,
            onDelete: handleDelete,
        }),
        [types] // eslint-disable-line react-hooks/exhaustive-deps
    );

    // Tách 2 bước: countPool = đã lọc mọi thứ TRỪ tab trạng thái (để đếm số trên
    // tab), filtered = countPool lọc tiếp theo tab đang chọn.
    const countPool = useMemo(
        () => filterPrograms(programs, { status: "all", keyword, type: typeFilter, camp: campFilter, enabledOnly }),
        [programs, keyword, typeFilter, campFilter, enabledOnly]
    );
    const filtered = useMemo(() => filterPrograms(countPool, { status }), [countPool, status]);

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="text-xl font-bold">Chương trình thưởng</h1>
                    <p className="text-sm text-content-3 mt-1 max-w-3xl">
                        Mỗi dòng = 1 đợt (8/8, 9/9, 12/12…). Thưởng chia theo lô ngày mua, mua sớm được trước, hết ngân sách thì trượt.
                        Chiến dịch X2 kiểu cũ (nhân inline) vẫn ở menu "Chiến dịch X2 (cũ)".
                    </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <Tooltip title="Tải lại danh sách">
                        <Button icon={<FontAwesomeIcon icon={faRotate} />} onClick={fetchAll} loading={loading} />
                    </Tooltip>
                    <Button type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={() => navigate("/reward-program/create")}>
                        Tạo đợt mới
                    </Button>
                </div>
            </div>

            <RewardProgramStats programs={programs} />

            <RewardProgramFilters
                programs={programs} countPool={countPool}
                status={status} onStatusChange={setStatus}
                keyword={keyword} onKeywordChange={setKeyword}
                type={typeFilter} onTypeChange={setTypeFilter}
                camp={campFilter} onCampChange={setCampFilter}
                enabledOnly={enabledOnly} onEnabledOnlyChange={setEnabledOnly}
                typeLabels={types} showing={filtered.length}
            />

            <div className="bg-surface border border-line rounded-lg shadow-sm">
                <Table
                    size="small" dataSource={filtered} rowKey="id" columns={columns}
                    pagination={false} loading={loading} scroll={{ x: "max-content" }}
                    locale={{ emptyText: <Empty description={programs.length ? "Không đợt nào khớp bộ lọc" : "Chưa có đợt thưởng nào"} /> }}
                />
            </div>
        </div>
    );
}

export default RewardProgramList;
